/**
 * @deprecated This file is deprecated and in migration to the new analytics architecture.
 * @see {@link ../../../core/analytics/README.md} for the new analytics system.
 * @todo Migrate to the new analytics system in src/core/analytics/
 */
import * as amplitude from "@amplitude/analytics-browser";

import { PartialWindow } from "@func-core/core/types";
import {
  getOrCreateAnonymousAnalyticsId,
  identifyUserInAnalytics,
} from "@func-core/core/utils/browser-features";
import { getExperimentsToTrack } from "@utils/analytics";
import { fetchApiJson } from "@utils/request";
import { logWarning, rollbar, RollbarLogArgument } from "@utils/rollbar";
import { getUtmsFromQueryParams } from "@utils/utm";

type IdentifyResponse = {
  user_id: string;
};

type AdapterOptions = {
  window?: PartialWindow;
};

type AnalyticsAdapter = {
  initializeAnalytics: () => Promise<boolean>;
  identify: (resultUuid: string) => Promise<IdentifyResponse | null>;
  identifyInBrowser: (userId: string, traits?: Record<string, unknown>) => void;
  trackEvent: (event: string, properties?: Record<string, unknown>) => void;
};

const isWindowDefined = typeof window !== "undefined";

export const createAnalyticsAdapter = ({
  window: windowObj = isWindowDefined ? (window as PartialWindow) : ({} as PartialWindow),
}: AdapterOptions = {}): AnalyticsAdapter => {
  const initializeAnalytics = async (): Promise<boolean> => {
    try {
      if (!isWindowDefined) return false;

      const apiKey = process.env.GATSBY_AMPLITUDE_API_KEY;
      const serverUrl = process.env.GATSBY_AMPLITUDE_SERVER_URL;
      const deviceId = getOrCreateAnonymousAnalyticsId(windowObj);

      if (!apiKey) {
        rollbar.info("Amplitude API key is not set");

        return false;
      }

      await amplitude.init(apiKey, undefined, {
        autocapture: false,
        serverUrl,
        deviceId,
      }).promise;

      return true;
    } catch (error) {
      rollbar.error("Failed to initialize Amplitude", error as RollbarLogArgument);

      return false;
    }
  };

  const identify = async (resultUuid: string): Promise<IdentifyResponse | null> => {
    if (!process.env.GATSBY_API_BASE) {
      throw new Error("Missing GATSBY_API_BASE environment variable");
    }

    try {
      return await fetchApiJson<IdentifyResponse>(
        `${process.env.GATSBY_API_BASE}/results/${resultUuid}/identify/`,
        { method: "POST" }
      );
    } catch (error) {
      logWarning("Analytics identify failed", error as Error, {
        context: "identify",
      });

      return null;
    }
  };

  const identifyInBrowser = (userId: string, traits: Record<string, unknown> = {}): void => {
    identifyUserInAnalytics(windowObj, userId, traits);

    const identifyEvent = new amplitude.Identify();
    amplitude.setUserId(userId);
    amplitude.identify(identifyEvent);
  };

  const trackEvent = (event: string, properties: Record<string, unknown> = {}): void => {
    if (!isWindowDefined) return;

    const experimentsList = getExperimentsToTrack();
    const utmProperties = getUtmsFromQueryParams();
    const page = windowObj.location.href;

    amplitude.track(event, { page, ...properties, experimentsList, ...utmProperties });
  };

  return {
    initializeAnalytics,
    identify,
    identifyInBrowser,
    trackEvent,
  };
};
